import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import FirebaseService from 'services/FirebaseService'
import { 
    apiGetSalesProductById, 
    apiGetAdminUserById,
    apiGetUserById
} from 'services/SalesService'

// Async thunk for getting all Firebase chats
export const getFirebaseChats = createAsyncThunk(
    'salesChats/getFirebaseChats',
    async (data, { rejectWithValue }) => {
        try {
            // Get chats directly from Firebase
            const chats = await FirebaseService.getAllChats()
            
            // Apply filters if provided
            let filteredChats = chats
            
            if (data?.query) {
                const searchTerm = data.query.toLowerCase()
                filteredChats = chats.filter(chat => {
                    // Search in participants, product name, or last message
                    const participantNames = chat.participants?.map(p => p.name?.toLowerCase()).join(' ') || ''
                    const productName = chat.productInfo?.name?.toLowerCase() || ''
                    const lastMessage = chat.lastMessage?.toLowerCase() || ''
                    
                    return participantNames.includes(searchTerm) ||
                           productName.includes(searchTerm) ||
                           lastMessage.includes(searchTerm)
                })
            }
            
            // Apply sorting
            if (data?.sort) {
                const { key, order } = data.sort
                const sortOrder = order === 'desc' ? -1 : 1
                
                filteredChats.sort((a, b) => {
                    let aValue = a[key] || 0
                    let bValue = b[key] || 0
                    
                    if (typeof aValue === 'string') {
                        return aValue.localeCompare(bValue) * sortOrder
                    }
                    return (aValue - bValue) * sortOrder
                })
            }
            
            // Apply pagination
            const pageIndex = data?.pageIndex || 1
            const pageSize = data?.pageSize || 10
            const skip = (pageIndex - 1) * pageSize
            const paginatedChats = filteredChats.slice(skip, skip + parseInt(pageSize))
            
            return {
                data: paginatedChats,
                total: filteredChats.length,
                pageIndex,
                pageSize
            }
        } catch (error) {
            return rejectWithValue(error.message)
        }
    }
)

// Async thunk for getting Firebase chat statistics
export const getFirebaseChatStats = createAsyncThunk(
    'salesChats/getFirebaseChatStats',
    async (_, { rejectWithValue }) => {
        try {
            const stats = await FirebaseService.getChatStatistics()
            return stats
        } catch (error) {
            return rejectWithValue(error.message)
        }
    }
)

// Async thunk for getting specific Firebase chat details
export const getFirebaseChatById = createAsyncThunk(
    'salesChats/getFirebaseChatById',
    async (chatId, { rejectWithValue }) => {
        try {
            const chatDetails = await FirebaseService.getChatDetails(chatId)
            
            if (!chatDetails) {
                throw new Error('Chat not found')
            }
            
            return chatDetails
        } catch (error) {
            return rejectWithValue(error.message)
        }
    }
)

// Enhanced async thunk for getting chat details with backend data enrichment
export const getEnhancedChatDetails = createAsyncThunk(
    'salesChats/getEnhancedChatDetails',
    async (chatId, { rejectWithValue }) => {
        try {
            // First get Firebase chat details
            const chatDetails = await FirebaseService.getChatDetails(chatId)
            
            if (!chatDetails) {
                throw new Error('Chat not found')
            }
            
            // Extract participant IDs and product ID from the chat
            const participantIds = chatDetails.participantIds || []
            const productId = chatDetails.productId
            
            // Fetch enhanced data from backend
            const enhancedData = {
                ...chatDetails,
                enhancedParticipants: [],
                enhancedProductInfo: null
            }
            
            // Fetch participant details from backend
            if (participantIds.length > 0) {
                
                const participantPromises = participantIds.map(async (participantId) => {
                    // 'support' is a pseudo-participant (WEAR IT staff), not a
                    // user id — looking it up only produced 400s in the console.
                    if (participantId === 'support') {
                        return { id: 'support', name: 'WearIt', username: 'WearIt', role: 'support' }
                    }
                    // Try multiple API endpoints to fetch user data
                    let userData = null
                    let dataSource = 'unknown'
                    let errorLog = []

                    // First try admin user API
                    try {
                        const response = await apiGetAdminUserById(participantId)
                        userData = response.data.data // Access the nested data object
                        dataSource = 'admin'
                    } catch (adminError) {
                        errorLog.push(`Admin API failed: ${adminError.message}`)
                        
                        // Try regular user API
                        try {
                            const response = await apiGetUserById(participantId)
                            userData = response.data.data // Access the nested data object
                            dataSource = 'user'
                        } catch (userError) {
                            errorLog.push(`User API failed: ${userError.message}`)
                        }
                    }
                    
                    // If we got backend data, use it with fallback to Firebase
                    if (userData) {
                        const firebaseData = chatDetails.participants?.find(p => p.id === participantId)
                        const enhancedParticipant = {
                            id: participantId,
                            // Prioritize backend data but keep Firebase as fallback
                            username: userData.username || userData.email?.split('@')[0] || firebaseData?.name || firebaseData?.username,
                            name: userData.profile?.firstName && userData.profile?.lastName 
                                ? `${userData.profile.firstName} ${userData.profile.lastName}`.trim()
                                : userData.profile?.firstName || userData.profile?.lastName || firebaseData?.name,
                            email: userData.email || firebaseData?.email,
                            phone: userData.phone || firebaseData?.phone,
                            profilePicture: userData.profile?.avatar || firebaseData?.avatar,
                            role: userData.userType || firebaseData?.role || 'user',
                            isFromBackend: true,
                            dataSource: dataSource,
                            originalData: userData,
                            fallbackData: firebaseData,
                            errorLog: errorLog
                        }
                        return enhancedParticipant
                    } else {
                        // Return Firebase data as fallback
                        const firebaseData = chatDetails.participants?.find(p => p.id === participantId)
                        const fallbackParticipant = {
                            id: participantId,
                            username: firebaseData?.username || firebaseData?.name || `user_${participantId.slice(-4)}`,
                            name: firebaseData?.name || firebaseData?.username,
                            email: firebaseData?.email,
                            phone: firebaseData?.phone,
                            profilePicture: firebaseData?.avatar,
                            role: firebaseData?.role || 'user',
                            isFromFirebase: true,
                            dataSource: 'firebase',
                            originalData: firebaseData,
                            errorLog: errorLog
                        }
                        return fallbackParticipant
                    }
                })
                
                enhancedData.enhancedParticipants = await Promise.all(participantPromises)
            } else {
                // If no participant IDs from chat ID parsing, try to get from participantsInfo
                if (chatDetails.participantsInfo && Object.keys(chatDetails.participantsInfo).length > 0) {
                    
                    const participantPromises = Object.keys(chatDetails.participantsInfo).map(async (participantId) => {
                        // Skip lookup for the 'support' pseudo-participant.
                        if (participantId === 'support') {
                            return { id: 'support', name: 'WearIt', username: 'WearIt', role: 'support' }
                        }
                        let userData = null
                        let dataSource = 'unknown'
                        let errorLog = []

                        // Try admin user API
                        try {
                            const response = await apiGetAdminUserById(participantId)
                            userData = response.data.data // Access the nested data object
                            dataSource = 'admin'
                        } catch (adminError) {
                            errorLog.push(`Admin API failed: ${adminError.message}`)
                            
                            // Try regular user API
                            try {
                                const response = await apiGetUserById(participantId)
                                userData = response.data.data // Access the nested data object
                                dataSource = 'user'
                            } catch (userError) {
                                errorLog.push(`User API failed: ${userError.message}`)
                            }
                        }
                        
                        if (userData) {
                            const firebaseData = chatDetails.participantsInfo[participantId]
                            return {
                                id: participantId,
                                username: userData.username || userData.email?.split('@')[0] || firebaseData?.name,
                                name: userData.profile?.firstName && userData.profile?.lastName 
                                    ? `${userData.profile.firstName} ${userData.profile.lastName}`.trim()
                                    : userData.profile?.firstName || userData.profile?.lastName || firebaseData?.name,
                                email: userData.email || firebaseData?.email,
                                phone: userData.phone || firebaseData?.phone,
                                profilePicture: userData.profile?.avatar || firebaseData?.avatar,
                                role: userData.userType || 'user',
                                isFromBackend: true,
                                dataSource: dataSource,
                                originalData: userData,
                                fallbackData: firebaseData,
                                errorLog: errorLog
                            }
                        } else {
                            const firebaseData = chatDetails.participantsInfo[participantId]
                            return {
                                id: participantId,
                                username: firebaseData?.name || `user_${participantId.slice(-4)}`,
                                name: firebaseData?.name,
                                email: firebaseData?.email,
                                phone: firebaseData?.phone,
                                profilePicture: firebaseData?.avatar,
                                role: 'user',
                                isFromFirebase: true,
                                dataSource: 'firebase',
                                originalData: firebaseData,
                                errorLog: errorLog
                            }
                        }
                    })
                    
                    enhancedData.enhancedParticipants = await Promise.all(participantPromises)
                }
            }
            
            // Fetch product details from backend
            if (productId) {
                try {
                    const response = await apiGetSalesProductById(productId)
                    // API returns { status, data: { ...product }, message }
                    const productData = response.data?.data || response.data || {}
                    enhancedData.enhancedProductInfo = {
                        id: productId,
                        ...productData,
                        isFromBackend: true
                    }
                } catch (error) {
                    // Keep Firebase product info as fallback
                    enhancedData.enhancedProductInfo = {
                        id: productId,
                        isFromFirebase: true,
                        ...chatDetails.productInfo
                    }
                }
            }
            
            return enhancedData
            
        } catch (error) {
            return rejectWithValue(error.message)
        }
    }
)

export const initialTableData = {
    total: 0,
    pageIndex: 1,
    pageSize: 10,
    query: '',
    sort: {
        order: 'desc',
        key: 'lastMessageTimestamp'
    }
}

export const initialFilterData = {
    status: ''
}

const dataSlice = createSlice({
    name: 'salesChats/data',
    initialState: {
        loading: false,
        chatsLoading: false,
        chatsList: [],
        tableData: initialTableData,
        filterData: initialFilterData,
        selectedChat: null,
        chatDetails: null,
        chatStats: {
            totalChats: 0,
            activeChats: 0,
            chatsWithOffers: 0,
            finalizedChats: 0,
            totalMessages: 0,
            totalOffers: 0,
            avgMessagesPerChat: 0,
            engagementRate: 0
        },
        error: null
    },
    reducers: {
        updateChatsTableData: (state, action) => {
            state.tableData = action.payload
        },
        updateChatsFilterData: (state, action) => {
            state.filterData = action.payload
        },
        setSelectedChat: (state, action) => {
            state.selectedChat = action.payload
        },
        clearSelectedChat: (state) => {
            state.selectedChat = null
            state.chatDetails = null
        },
        clearError: (state) => {
            state.error = null
        },
        updateRealTimeChats: (state, action) => {
            // Update chats list with real-time data
            state.chatsList = action.payload
            state.tableData.total = action.payload.length
            state.chatsLoading = false
            state.error = null
        },
        setChatsLoading: (state, action) => {
            state.chatsLoading = action.payload
        },
        updateStats: (state, action) => {
            state.chatStats = {
                ...state.chatStats,
                ...action.payload
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Get Firebase chats
            .addCase(getFirebaseChats.pending, (state) => {
                state.chatsLoading = true
                state.error = null
            })
            .addCase(getFirebaseChats.fulfilled, (state, action) => {
                state.chatsLoading = false
                state.chatsList = action.payload.data
                state.tableData.total = action.payload.total
                state.tableData.pageIndex = action.payload.pageIndex
                state.tableData.pageSize = action.payload.pageSize
                state.error = null
            })
            .addCase(getFirebaseChats.rejected, (state, action) => {
                state.chatsLoading = false
                state.chatsList = []
                state.error = action.payload
            })
            
            // Get Firebase chat statistics
            .addCase(getFirebaseChatStats.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(getFirebaseChatStats.fulfilled, (state, action) => {
                state.loading = false
                state.chatStats = action.payload
                state.error = null
            })
            .addCase(getFirebaseChatStats.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Get Firebase chat by ID
            .addCase(getFirebaseChatById.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(getFirebaseChatById.fulfilled, (state, action) => {
                state.loading = false
                state.chatDetails = action.payload
                state.error = null
            })
            .addCase(getFirebaseChatById.rejected, (state, action) => {
                state.loading = false
                state.chatDetails = null
                state.error = action.payload
            })
            
            // Get enhanced chat details (Firebase + Backend)
            .addCase(getEnhancedChatDetails.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(getEnhancedChatDetails.fulfilled, (state, action) => {
                state.loading = false
                state.chatDetails = action.payload
                state.error = null
            })
            .addCase(getEnhancedChatDetails.rejected, (state, action) => {
                state.loading = false
                state.chatDetails = null
                state.error = action.payload
            })
    }
})

export const {
    updateChatsTableData,
    updateChatsFilterData,
    setSelectedChat,
    clearSelectedChat,
    clearError,
    updateRealTimeChats,
    setChatsLoading,
    updateStats
} = dataSlice.actions

export default dataSlice.reducer 